/**
 * Keepsy FAQ: Live-Suche, Deep-Links und Preise aus pricing.js.
 *
 * - Live-Suche ueber Frage, Antwort und Stichworte (data-keywords). Gross- und
 *   Kleinschreibung, Umlaute und ss werden ignoriert, "ue" findet auch "ü".
 *   Treffer in der Frage bekommen einen gelben Marker. Passt nur die Antwort,
 *   klappt die Frage waehrend der Suche auf und danach wieder zu.
 * - Deep-Links: faq.html#versand oeffnet die Frage, scrollt hin und setzt den Fokus
 *   auf die Frage (a11y S-45). Aufklappen schreibt die Adresse ohne Sprung um,
 *   "Link kopieren" legt den direkten Link in die Zwischenablage.
 * - Preise und Prozente kommen aus pricing.js, das HTML enthaelt dieselben Werte
 *   als Rueckfall ohne JavaScript.
 * - Die Garantien decken sich beim ersten Scrollen einmal auf (RESEARCH 7, Regel 6).
 *
 * Ohne JavaScript bleibt alles lesbar: Fragen klappen nativ (details), die Suche
 * ist dann ausgeblendet.
 * @module pages/faq
 */

import { PRICES, BUNDLE, cartTotals, formatEuro, toCents, fromCents, fourBonusCents, fourBonusMinCount, shippingCents, freeShippingFromCents, freeShippingMinCount } from '@keepsy/pricing.js'
import { NBSP, debounce } from '@keepsy/format.js'
import { toast } from '@keepsy/ui-toast.js'
import { initReveal } from '@keepsy/ui-reveal.js'

const list = document.querySelector('[data-faq-list]')
const searchBox = document.querySelector('[data-faq-search]')
const input = searchBox ? searchBox.querySelector('input[type="search"]') : null
const countEl = document.querySelector('[data-faq-count]')
const statusEl = document.querySelector('[data-faq-status]')
const emptyEl = document.querySelector('[data-faq-empty]')
const clearInline = searchBox ? searchBox.querySelector('.k-search__clear') : null

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

/** Programmatisch geoeffnete Fragen, deren toggle-Event die Adresse nicht aendern soll. */
const silentToggles = new WeakSet()
/** Von der Suche aufgeklappte Fragen, die beim Leeren wieder zuklappen. */
const autoOpened = new Set()
/** Bis zu so vielen Treffern klappt die Suche Antwort-Treffer automatisch auf. */
const AUTO_OPEN_MAX = 3

/* --------------------------------------------------------------------------
   Text falten: klein, ohne Akzente, ss statt ß. Mit Index-Karte fuer Marker.
   -------------------------------------------------------------------------- */

/**
 * @param {string} ch ein Zeichen
 * @returns {string}
 */
function foldChar(ch) {
  const lower = ch.toLowerCase()
  if (lower === 'ß') return 'ss'
  return lower.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

/**
 * Faltet Text und merkt sich zu jedem gefalteten Zeichen die Position im Original.
 * @param {string} text
 * @returns {{ text: string, map: number[] }}
 */
function foldWithMap(text) {
  let out = ''
  const map = []
  let index = 0
  for (const ch of text) {
    const folded = foldChar(ch)
    for (let i = 0; i < folded.length; i += 1) map.push(index)
    out += folded
    index += ch.length
  }
  return { text: out, map }
}

/** @param {string} text */
function fold(text) {
  return foldWithMap(text).text
}

/** "ue", "ae", "oe" als Umschreibung von ü, ä, ö: "rueckgabe" findet "Rückgabe". */
function transliterate(text) {
  return text.replace(/ae/g, 'a').replace(/oe/g, 'o').replace(/ue/g, 'u')
}

/**
 * Zerlegt die Suchanfrage in gefaltete Woerter.
 * @param {string} raw
 * @returns {string[]}
 */
function tokenize(raw) {
  return fold(String(raw || ''))
    .split(/[\s,.;:!?„“"'()/]+/)
    .filter(Boolean)
}

/**
 * Varianten eines Suchworts: das Wort selbst, bei Umschreibung zusaetzlich ohne.
 * @param {string} token
 * @returns {string[]}
 */
function variants(token) {
  const alt = transliterate(token)
  return alt !== token ? [token, alt] : [token]
}

/**
 * @param {string} haystack gefalteter Text
 * @param {string} token gefaltetes Suchwort
 */
function tokenMatches(haystack, token) {
  return variants(token).some((v) => haystack.includes(v))
}

/* --------------------------------------------------------------------------
   Preise aus pricing.js (einzige Quelle fuer Zahlen)
   -------------------------------------------------------------------------- */

/** Prozent des Duo-Vorteils ("25 %", jede zweite Hülle), null wenn abgeschaltet. */
function duoPercentText() {
  return BUNDLE.percentOff ? `${BUNDLE.percentOff}${NBSP}%` : null
}

/** Glatte Betraege ohne Nachkommastellen ("30 €"), sonst wie formatEuro. */
function wholeEuro(value) {
  return Number.isInteger(value) ? `${value}${NBSP}€` : formatEuro(value)
}

/**
 * Preis je Hülle für den 4er-Vorteil: glatt "15 €", sonst aufgerundet "unter 15 €" (nie zu günstig).
 * @param {number} afterCents Warenwert nach allen Rabatten in Cent
 * @param {number} qty
 * @returns {string}
 */
function perCaseText(afterCents, qty) {
  if (!(qty > 0) || !(afterCents > 0)) return ''
  const exact = afterCents % qty === 0 && (afterCents / qty) % 100 === 0
  return exact ? `${afterCents / qty / 100}${NBSP}€` : `unter ${Math.ceil(afterCents / qty / 100)}${NBSP}€`
}

function fillPrices() {
  const duo = cartTotals([{ magsafe: false, qty: 1 }, { magsafe: false, qty: 1 }])
  const min = fourBonusMinCount()
  const four = cartTotals([{ magsafe: false, qty: min }])
  const fourAfter = toCents(four.subtotal) - toCents(four.discount)
  const values = {
    base: formatEuro(PRICES.base),
    magsafe: formatEuro(PRICES.magsafe),
    shipping: formatEuro(fromCents(shippingCents())),
    freeFrom: wholeEuro(fromCents(freeShippingFromCents())),
    freeCount: String(freeShippingMinCount()),
    duoTotal: formatEuro(duo.total),
    duoEach: formatEuro(duo.total / 2),
    tier2: duoPercentText(),
    fourMin: String(min),
    fourBonus: formatEuro(fromCents(fourBonusCents())),
    fourTotal: formatEuro(fromCents(fourAfter)),
    fourWas: formatEuro(four.subtotal),
    fourEach: perCaseText(fourAfter, min),
  }
  document.querySelectorAll('[data-price]').forEach((el) => {
    const value = values[el.dataset.price]
    if (value) el.textContent = value
  })
  // Ohne 4er-Vorteil (Betrag 0) fällt sein Absatz weg
  if (!fourBonusCents()) document.querySelectorAll('[data-four-faq]').forEach((el) => { el.hidden = true })
}

/* --------------------------------------------------------------------------
   Eintraege
   -------------------------------------------------------------------------- */

/**
 * @typedef {Object} Entry
 * @property {HTMLDetailsElement} el
 * @property {HTMLElement} question
 * @property {string} questionText Originaltext der Frage
 * @property {{ text: string, map: number[] }} questionFold
 * @property {string} haystack gefalteter Suchtext (Frage, Antwort, Stichworte)
 */

/** @type {Entry[]} */
let entries = []

function buildEntries() {
  entries = Array.from(list.querySelectorAll('[data-faq-item]')).map((el) => {
    const question = el.querySelector('[data-faq-question]')
    const answer = el.querySelector('[data-faq-answer]')
    const questionText = question.textContent.trim()
    const keywords = fold(el.dataset.keywords || '')
    const haystack = [
      fold(questionText),
      fold(answer ? answer.textContent : ''),
      keywords,
      transliterate(keywords),
    ].join(' ')
    return { el, question, questionText, questionFold: foldWithMap(questionText), haystack }
  })
}

/* --------------------------------------------------------------------------
   Marker in der Frage
   -------------------------------------------------------------------------- */

/**
 * Setzt die Frage mit gelben Markern an den Fundstellen neu.
 * @param {Entry} entry
 * @param {string[]} tokens
 */
function highlight(entry, tokens) {
  const { text: folded, map } = entry.questionFold
  const original = entry.questionText
  const ranges = []
  for (const token of tokens) {
    for (const v of variants(token)) {
      let from = folded.indexOf(v)
      if (from === -1) continue
      while (from !== -1) {
        const start = map[from]
        const endIndex = map[from + v.length - 1]
        ranges.push([start, endIndex + 1])
        from = folded.indexOf(v, from + v.length)
      }
      break
    }
  }
  if (!ranges.length) {
    if (entry.question.childElementCount) entry.question.textContent = original
    return
  }
  ranges.sort((a, b) => a[0] - b[0])
  const merged = []
  for (const range of ranges) {
    const last = merged[merged.length - 1]
    if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1])
    else merged.push([...range])
  }
  const fragment = document.createDocumentFragment()
  let cursor = 0
  for (const [start, end] of merged) {
    if (start > cursor) fragment.append(original.slice(cursor, start))
    const mark = document.createElement('mark')
    mark.className = 'k-faq__hit'
    mark.textContent = original.slice(start, end)
    fragment.append(mark)
    cursor = end
  }
  if (cursor < original.length) fragment.append(original.slice(cursor))
  entry.question.replaceChildren(fragment)
}

/* --------------------------------------------------------------------------
   Suche
   -------------------------------------------------------------------------- */

const announce = debounce((text) => {
  if (statusEl) statusEl.textContent = text
}, 500)

function countText(shown, total, raw) {
  const query = raw.trim()
  if (!query) return `Alle ${total} Fragen`
  if (shown === 0) return `Keine Frage passt zu „${query}“`
  return shown === 1
    ? `1 von ${total} Fragen passt zu „${query}“`
    : `${shown} von ${total} Fragen passen zu „${query}“`
}

/**
 * Oeffnet eine Frage ohne Adresswechsel.
 * @param {HTMLDetailsElement} el
 */
function openSilently(el) {
  if (el.open) return
  silentToggles.add(el)
  el.open = true
}

/**
 * Filtert die Liste nach der Anfrage.
 * @param {string} raw
 */
function applyFilter(raw) {
  const tokens = tokenize(raw)
  const searching = tokens.length > 0
  const longEnough = fold(raw).replace(/\s+/g, '').length >= 3
  const matches = entries.map((entry) => !searching || tokens.every((t) => tokenMatches(entry.haystack, t)))
  const shown = matches.filter(Boolean).length
  // Nur bei wenigen Treffern aufklappen, sonst wird die Liste bei Allerweltswoertern lang
  const mayOpen = searching && longEnough && shown <= AUTO_OPEN_MAX

  entries.forEach((entry, index) => {
    const match = matches[index]
    if (searching) entry.el.dataset.filtered = match ? 'in' : 'out'
    else delete entry.el.dataset.filtered
    highlight(entry, match ? tokens : [])

    const titleMatch = searching && tokens.every((t) => tokenMatches(entry.questionFold.text, t))
    if (mayOpen && match && !titleMatch) {
      if (!entry.el.open) {
        openSilently(entry.el)
        autoOpened.add(entry.el)
      }
    } else if (autoOpened.has(entry.el)) {
      autoOpened.delete(entry.el)
      entry.el.open = false
    }
  })

  const text = countText(shown, entries.length, raw)
  if (countEl) countEl.textContent = text
  announce(searching ? text : '')
  if (emptyEl) emptyEl.hidden = !(searching && shown === 0)
  list.hidden = searching && shown === 0
  if (clearInline) clearInline.hidden = !raw
}

function clearSearch({ focus = true } = {}) {
  if (!input) return
  input.value = ''
  applyFilter('')
  if (focus) input.focus()
}

function initSearch() {
  if (!searchBox || !input) return
  searchBox.hidden = false
  input.addEventListener('input', () => applyFilter(input.value))
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && input.value) {
      event.preventDefault()
      clearSearch()
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const first = entries.find((entry) => entry.el.dataset.filtered !== 'out')
      if (!first) return
      openSilently(first.el)
      first.el.querySelector('summary').focus()
    }
  })
  document.querySelectorAll('[data-faq-clear]').forEach((button) => {
    button.addEventListener('click', () => clearSearch())
  })
  // Wer per Zurueck-Taste wiederkommt, findet ggf. noch Text im Feld
  if (input.value) applyFilter(input.value)
}

/* --------------------------------------------------------------------------
   Deep-Links
   -------------------------------------------------------------------------- */

function itemFromHash() {
  let id
  try {
    id = decodeURIComponent(window.location.hash.slice(1))
  } catch {
    // Kaputte Prozent-Kodierung (z. B. faq.html#%E0): kein Treffer, Seite laeuft weiter
    return null
  }
  if (!id) return null
  const el = document.getElementById(id)
  return el && el.matches('[data-faq-item]') ? el : null
}

/**
 * Oeffnet die Frage aus der Adresse, scrollt hin und setzt den Fokus.
 * @param {{ smooth?: boolean }} [options]
 */
function openFromHash(options = {}) {
  const el = itemFromHash()
  if (!el) return
  if (el.dataset.filtered === 'out') clearSearch({ focus: false })
  openSilently(el)
  autoOpened.delete(el)
  requestAnimationFrame(() => {
    el.scrollIntoView({ block: 'start', behavior: options.smooth && !reducedMotion.matches ? 'smooth' : 'auto' })
    el.querySelector('summary').focus({ preventScroll: true })
  })
}

function initDeepLinks() {
  window.addEventListener('hashchange', () => openFromHash({ smooth: true }))

  // Gleicher Anker noch einmal geklickt: hashchange feuert nicht, also selbst oeffnen
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#"]')
    if (!link || link.getAttribute('href') !== window.location.hash) return
    if (!itemFromHash()) return
    event.preventDefault()
    openFromHash({ smooth: true })
  })

  // Aufklappen schreibt die Adresse um (ohne Sprung), Zuklappen nimmt sie wieder raus
  list.addEventListener('toggle', (event) => {
    const el = event.target
    if (!(el instanceof HTMLDetailsElement) || !el.matches('[data-faq-item]')) return
    if (silentToggles.has(el)) {
      silentToggles.delete(el)
      return
    }
    if (!el.open) autoOpened.delete(el)
    const base = `${window.location.pathname}${window.location.search}`
    if (el.open) history.replaceState(history.state, '', `${base}#${el.id}`)
    else if (window.location.hash === `#${el.id}`) history.replaceState(history.state, '', base)
  }, true)

  if (itemFromHash()) openFromHash()
}

/* --------------------------------------------------------------------------
   Link kopieren
   -------------------------------------------------------------------------- */

async function copyLink(entry) {
  const link = `${window.location.origin}${window.location.pathname}#${entry.el.id}`
  try {
    if (!navigator.clipboard || !window.isSecureContext) throw new Error('Zwischenablage nicht verfuegbar')
    await navigator.clipboard.writeText(link)
    toast.show({ title: 'Link kopiert.', text: entry.questionText, tone: 'success' })
  } catch {
    history.replaceState(history.state, '', `#${entry.el.id}`)
    toast.show({
      title: 'Kopieren hat nicht geklappt.',
      text: 'Der Link zur Frage steht jetzt oben in der Adresszeile.',
      tone: 'error',
    })
  }
}

function initCopyButtons() {
  for (const entry of entries) {
    const answer = entry.el.querySelector('[data-faq-answer]')
    if (!answer) continue
    const tools = document.createElement('div')
    tools.className = 'k-faq__tools'
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'k-faq__copy'
    button.textContent = 'Link kopieren'
    button.setAttribute('aria-label', `Link zur Frage „${entry.questionText}“ kopieren`)
    button.addEventListener('click', () => copyLink(entry))
    tools.append(button)
    answer.append(tools)
  }
}

/* --------------------------------------------------------------------------
   Start
   -------------------------------------------------------------------------- */

fillPrices()
if (list) {
  buildEntries()
  initCopyButtons()
  initSearch()
  initDeepLinks()
}
initReveal()
